/**
 * SOAR Product Main — vanilla custom element
 * Handles:
 *   - Gallery thumbnail clicks (swap main image)
 *   - Variant picker (update price, URL, current variant id)
 *   - Variant image swapping when variant has its own media
 *   - Quantity inc/dec
 *   - Add to cart via /cart/add.js then dispatches `cart:add`
 *   - Accordion toggles
 *
 * Self-contained. No imports.
 */
class SoarProductMain extends HTMLElement {
  connectedCallback() {
    try {
      this.productData = JSON.parse(this.querySelector('[data-soar-product-json]')?.textContent || 'null');
    } catch (err) {
      console.error('SoarProductMain: failed to parse product JSON', err);
      this.productData = null;
    }

    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.currentVariantId = this.dataset.currentVariantId ? Number(this.dataset.currentVariantId) : null;

    this.priceEl = this.querySelector('[data-soar-price]');
    this.compareEl = this.querySelector('[data-soar-compare-price]');
    this.saleBadge = this.querySelector('[data-soar-sale-badge]');
    this.stockEl = this.querySelector('[data-soar-stock]');
    this.atcBtn = this.querySelector('[data-soar-atc]');
    this.errorEl = this.querySelector('[data-soar-error]');
    this.qtyInput = this.querySelector('[data-soar-qty-input]');

    this.bindGallery();
    this.bindVariantOptions();
    this.bindQuantity();
    this.bindAddToCart();
    this.bindUpsell();
    this.bindAccordions();

    // Sync UI state to whatever variant is current
    if (this.currentVariantId) {
      this.applyVariant(this.findVariantById(this.currentVariantId), { pushHistory: false, swapMedia: false });
    }
  }

  /* ------------------------------------------------------------------ */
  /* Gallery                                                             */
  /* ------------------------------------------------------------------ */
  bindGallery() {
    this.stage = this.querySelector('[data-soar-media-stage]');
    this.slides = Array.from(this.querySelectorAll('[data-soar-media-slide]'));
    const thumbs = Array.from(this.querySelectorAll('[data-soar-thumb]'));

    // Which option index is the color? (so we can filter media by variant via alt text)
    this.colorOptionIndex = null;
    if (this.productData && Array.isArray(this.productData.options)) {
      const aliases = ['color', 'colour', 'colorway', 'colour way'];
      this.productData.options.forEach((name, idx) => {
        if (this.colorOptionIndex === null && aliases.indexOf(String(name).trim().toLowerCase()) !== -1) {
          this.colorOptionIndex = idx;
        }
      });
    }

    // Pin GIFs to the end of the gallery + filmstrip (they're never filtered out).
    const thumbsWrap = this.querySelector('.soar-product-main__thumbs');
    this.slides.filter((s) => s.dataset.isGif === 'true').forEach((s) => {
      if (this.stage) this.stage.appendChild(s);
    });
    if (thumbsWrap) {
      thumbs.filter((t) => t.dataset.isGif === 'true').forEach((t) => thumbsWrap.appendChild(t));
    }
    this.slides = Array.from(this.querySelectorAll('[data-soar-media-slide]'));

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const mediaId = thumb.dataset.mediaId;
        this.activateMedia(mediaId);
      });
    });
  }

  activateMedia(mediaId) {
    if (!mediaId) return;
    this.slides.forEach((slide) => {
      slide.dataset.active = (slide.dataset.mediaId === String(mediaId)) ? 'true' : 'false';
    });
    this.querySelectorAll('[data-soar-thumb]').forEach((thumb) => {
      thumb.dataset.active = (thumb.dataset.mediaId === String(mediaId)) ? 'true' : 'false';
    });
  }

  /* Show only the media (slides + thumbs) belonging to the selected variant,
     matched by the variant's color value appearing in each image's alt text. */
  filterMediaByColor(variant) {
    if (this.colorOptionIndex === null || !variant || !variant.options || !this.slides.length) return;
    const color = String(variant.options[this.colorOptionIndex] || '').trim().toLowerCase();
    const thumbs = Array.from(this.querySelectorAll('[data-soar-thumb]'));
    if (!color) return;

    // GIFs are always kept; color images are matched by alt text.
    const colorMatch = (el) => (el.dataset.mediaAlt || '').indexOf(color) !== -1;
    const keep = (el) => el.dataset.isGif === 'true' || colorMatch(el);
    // If no color image matches at all, don't hide everything — show all.
    if (!this.slides.some(colorMatch)) {
      this.slides.forEach((s) => { s.hidden = false; });
      thumbs.forEach((t) => { t.hidden = false; });
      return;
    }

    this.slides.forEach((s) => { s.hidden = !keep(s); });
    thumbs.forEach((t) => { t.hidden = !keep(t); });

    // Activate the variant's featured image if it's visible, else the first visible one.
    const featuredId = variant.featured_media && variant.featured_media.id;
    const visible = this.slides.filter((s) => !s.hidden);
    if (featuredId && visible.some((s) => s.dataset.mediaId === String(featuredId))) {
      this.activateMedia(featuredId);
    } else if (visible[0]) {
      this.activateMedia(visible[0].dataset.mediaId);
    }
  }

  /* ------------------------------------------------------------------ */
  /* Variant picker                                                      */
  /* ------------------------------------------------------------------ */
  bindVariantOptions() {
    const inputs = this.querySelectorAll('[data-soar-option-input]');
    inputs.forEach((input) => {
      input.addEventListener('change', () => this.onOptionChange());
    });
  }

  onOptionChange() {
    if (!this.productData) return;
    const selectedOptions = this.getSelectedOptions();
    const variant = this.productData.variants.find((v) => {
      return v.options.every((opt, idx) => opt === selectedOptions[idx]);
    });
    if (variant) {
      this.applyVariant(variant, { pushHistory: true, swapMedia: true });
    } else {
      this.setUnavailable();
    }
  }

  getSelectedOptions() {
    const options = [];
    const groups = this.querySelectorAll('[data-soar-option-group]');
    groups.forEach((group) => {
      const checked = group.querySelector('[data-soar-option-input]:checked');
      options.push(checked ? checked.value : null);
    });
    return options;
  }

  findVariantById(id) {
    if (!this.productData) return null;
    return this.productData.variants.find((v) => v.id === Number(id)) || null;
  }

  applyVariant(variant, { pushHistory = true, swapMedia = true } = {}) {
    if (!variant) {
      this.setUnavailable();
      return;
    }

    this.currentVariantId = variant.id;
    this.dataset.currentVariantId = variant.id;

    // Keep every product form's hidden id in sync (buy form + finance form) so
    // dynamic checkout buys the selected variant and the financing message matches.
    this.querySelectorAll('[data-soar-variant-id]').forEach((el) => { el.value = variant.id; });
    if (window.Shopify && Shopify.PaymentButton && typeof Shopify.PaymentButton.init === 'function') {
      try { Shopify.PaymentButton.init(); } catch (e) {}
    }

    // Update price display
    if (this.priceEl) {
      this.priceEl.textContent = this.formatMoney(variant.price);
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      this.priceEl.classList.toggle('soar-product-main__price--sale', !!onSale);
      if (this.compareEl) {
        if (onSale) {
          this.compareEl.textContent = this.formatMoney(variant.compare_at_price);
          this.compareEl.hidden = false;
        } else {
          this.compareEl.hidden = true;
        }
      }
      if (this.saleBadge) this.saleBadge.hidden = !onSale;
    }

    // Update selected-value labels (skip cross-link color groups whose label is
    // fixed to the chip_color metafield by Liquid).
    this.querySelectorAll('[data-soar-option-group]').forEach((group, idx) => {
      if (group.hasAttribute('data-soar-fixed-label')) return;
      const valueLabel = group.querySelector('[data-soar-option-current]');
      if (valueLabel) valueLabel.textContent = variant.options[idx] || '';
    });

    // Update add-to-cart state
    if (this.atcBtn) {
      const available = variant.available !== false;
      this.atcBtn.disabled = !available;
      this.atcBtn.dataset.unavailable = available ? 'false' : 'true';
      const labelEl = this.atcBtn.querySelector('[data-soar-atc-label]');
      if (labelEl) {
        labelEl.textContent = available
          ? (this.atcBtn.dataset.labelDefault || 'Add to Cart')
          : 'Sold Out';
      }
    }

    // Stock indicator
    if (this.stockEl) {
      const available = variant.available !== false;
      this.stockEl.classList.toggle('soar-product-main__stock--out', !available);
      const textEl = this.stockEl.querySelector('[data-soar-stock-text]');
      if (textEl) {
        textEl.textContent = available
          ? (this.dataset.stockInText || 'In stock — ships in 3-5 business days')
          : 'Currently unavailable';
      }
    }

    // Clear any prior error
    if (this.errorEl) this.errorEl.textContent = '';

    // Update URL ?variant=
    if (pushHistory && typeof window !== 'undefined' && window.history && window.history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    // Filter the gallery to the selected variant's images (matched by color in alt
    // text). Runs on initial load too, so the pre-selected variant's images show only.
    this.filterMediaByColor(variant);
  }

  setUnavailable() {
    if (this.atcBtn) {
      this.atcBtn.disabled = true;
      this.atcBtn.dataset.unavailable = 'true';
      const labelEl = this.atcBtn.querySelector('[data-soar-atc-label]');
      if (labelEl) labelEl.textContent = 'Unavailable';
    }
  }

  /* ------------------------------------------------------------------ */
  /* Quantity                                                            */
  /* ------------------------------------------------------------------ */
  bindQuantity() {
    const minus = this.querySelector('[data-soar-qty-minus]');
    const plus = this.querySelector('[data-soar-qty-plus]');
    if (!this.qtyInput) return;

    minus?.addEventListener('click', () => {
      const v = Math.max(1, (Number(this.qtyInput.value) || 1) - 1);
      this.qtyInput.value = String(v);
    });
    plus?.addEventListener('click', () => {
      const v = Math.max(1, (Number(this.qtyInput.value) || 1) + 1);
      this.qtyInput.value = String(v);
    });
    this.qtyInput.addEventListener('change', () => {
      const v = Math.max(1, Number(this.qtyInput.value) || 1);
      this.qtyInput.value = String(v);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Add to cart                                                         */
  /* ------------------------------------------------------------------ */
  bindAddToCart() {
    if (!this.atcBtn) return;
    this.atcBtn.addEventListener('click', (e) => {
      e.preventDefault();
      this.addToCart();
    });
  }

  async addToCart() {
    if (!this.currentVariantId) return;
    if (this.atcBtn.disabled) return;

    const quantity = Math.max(1, Number(this.qtyInput?.value) || 1);
    const defaultLabel = this.atcBtn.dataset.labelDefault || 'Add to Cart';
    const labelEl = this.atcBtn.querySelector('[data-soar-atc-label]');

    this.atcBtn.dataset.loading = 'true';
    if (labelEl) labelEl.textContent = 'Adding…';
    if (this.errorEl) this.errorEl.textContent = '';

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: this.currentVariantId, quantity }],
        }),
      });

      if (!res.ok) {
        let msg = 'Could not add to cart';
        try {
          const errBody = await res.json();
          msg = errBody.description || errBody.message || msg;
        } catch (_) { /* ignore */ }
        throw new Error(msg);
      }

      // Tell the side cart drawer to refresh + open.
      document.dispatchEvent(new CustomEvent('cart:add'));

      if (labelEl) labelEl.textContent = 'Added ✓';
      setTimeout(() => {
        if (labelEl) labelEl.textContent = defaultLabel;
      }, 1400);
    } catch (err) {
      console.error('SoarProductMain add-to-cart failed', err);
      if (this.errorEl) this.errorEl.textContent = err.message || 'Add to cart failed.';
      if (labelEl) labelEl.textContent = defaultLabel;
    } finally {
      this.atcBtn.dataset.loading = 'false';
    }
  }

  /* ------------------------------------------------------------------ */
  /* "Pairs well with" — fetch complementary recommendation + quick-add  */
  /* ------------------------------------------------------------------ */
  bindUpsell() {
    const slot = this.querySelector('[data-soar-pairs]');
    if (!slot || !slot.dataset.url) return;
    fetch(slot.dataset.url)
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const card = doc.querySelector('.soar-product-main__upsell');
        if (!card) return;
        slot.innerHTML = card.outerHTML;
        const btn = slot.querySelector('[data-soar-upsell-add]');
        if (btn) this.bindUpsellAdd(btn);
        this.bindPairsVariants(slot);
      })
      .catch(() => { /* no recommendations — leave empty */ });
  }

  /* Mini variant picker for the "Pairs well with" card (color chips + size pills) */
  bindPairsVariants(slot) {
    const dataEl = slot.querySelector('[data-soar-pairs-variants]');
    const groups = slot.querySelectorAll('[data-soar-pairs-opt]');
    if (!dataEl || !groups.length) return;
    let variants = [];
    try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }
    if (!variants.length) return;

    const btn = slot.querySelector('[data-soar-upsell-add]');
    const priceEl = slot.querySelector('[data-soar-upsell-price]');
    const img = slot.querySelector('.soar-product-main__upsell-img');
    const selected = [];

    const init = variants.find((v) => v.available) || variants[0];

    const update = () => {
      const v = variants.find((vv) => selected.every((s, i) => s == null || vv.options[i] === s));
      if (v) {
        if (btn) { btn.dataset.variantId = v.id; btn.disabled = !v.available; }
        if (priceEl) priceEl.textContent = this.formatMoney(v.price);
        if (img && v.featured_image && v.featured_image.src) { img.removeAttribute('srcset'); img.src = v.featured_image.src; }
      } else if (btn) {
        btn.disabled = true;
      }
    };

    groups.forEach((g) => {
      const pos = Number(g.dataset.optPos) - 1;
      selected[pos] = init ? init.options[pos] : null;
      g.querySelectorAll('[data-soar-pairs-val]').forEach((el) => {
        if (el.dataset.value === selected[pos]) el.classList.add('is-active');
        el.addEventListener('click', () => {
          g.querySelectorAll('[data-soar-pairs-val]').forEach((s) => s.classList.remove('is-active'));
          el.classList.add('is-active');
          selected[pos] = el.dataset.value;
          update();
        });
      });
    });
    update();
  }

  bindUpsellAdd(btn) {
    btn.addEventListener('click', async () => {
      const id = Number(btn.dataset.variantId);
      if (!id || btn.dataset.loading === 'true') return;
      const original = btn.textContent;
      btn.dataset.loading = 'true';
      btn.textContent = 'Adding…';
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id, quantity: 1 }] }),
        });
        if (!res.ok) throw new Error('add failed');
        document.dispatchEvent(new CustomEvent('cart:add'));
        btn.textContent = 'Added ✓';
        setTimeout(() => { btn.textContent = original; btn.dataset.loading = 'false'; }, 1400);
      } catch (err) {
        btn.textContent = original;
        btn.dataset.loading = 'false';
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* Accordions                                                          */
  /* ------------------------------------------------------------------ */
  bindAccordions() {
    const accordions = this.querySelectorAll('[data-soar-accordion]');
    accordions.forEach((acc) => {
      const trigger = acc.querySelector('[data-soar-accordion-trigger]');
      if (!trigger) return;
      trigger.addEventListener('click', () => {
        const isOpen = acc.dataset.open === 'true';
        acc.dataset.open = isOpen ? 'false' : 'true';
        trigger.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Helpers                                                             */
  /* ------------------------------------------------------------------ */
  formatMoney(cents) {
    if (cents == null || isNaN(cents)) return '';
    const dollars = (Number(cents) / 100).toFixed(2);
    return this.moneyFormat.replace(/\{\{\s*amount\s*\}\}/g, dollars);
  }
}

if (!customElements.get('soar-product-main')) {
  customElements.define('soar-product-main', SoarProductMain);
}
